import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link as MuiLink,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { studyAPI } from '../../services/api';
import { formatProjectLabel } from '../../utils/formatProjectLabel';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import CohortComparison from '../analysis/CohortComparison';

const StudyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [study, setStudy] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const canEdit = ['admin', 'lab_manager', 'lab_technician'].includes(currentUser?.role);

  // Study-level rollup (#67): totals summed from the per-project counts already
  // in the projects response — no extra query. specimen_count arrives as a string
  // (pg COUNT), so coerce before summing or it concatenates.
  const totalSpecimens = projects.reduce((sum, p) => sum + (Number(p.specimen_count) || 0), 0);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [studyRes, projectsRes] = await Promise.all([
          studyAPI.getById(id),
          studyAPI.getProjects(id),
        ]);
        setStudy(studyRes.data);
        setProjects(projectsRes.data || []);
        setError('');
      } catch (err) {
        console.error('Error fetching study', err);
        setError('Failed to load study');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]);

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete study "${study.name}"? Its ${projects.length} project(s) will be unassigned from the study, not deleted.`
      )
    ) {
      return;
    }
    try {
      await studyAPI.delete(id);
      toast.success('Study deleted; projects detached');
      navigate('/studies');
    } catch (err) {
      console.error('Error deleting study', err);
      toast.error('Failed to delete study');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !study) {
    return (
      <Box className="page-container">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Study not found'}
        </Alert>
        <Button component={Link} to="/studies" startIcon={<ArrowBackIcon />}>
          Back to Studies
        </Button>
      </Box>
    );
  }

  return (
    <Box className="study-detail page-container">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton component={Link} to="/studies" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              {study.name}
            </Typography>
            {study.study_number != null && (
              <Typography variant="caption" color="text.secondary">
                Study #{study.study_number}
              </Typography>
            )}
          </Box>
        </Box>
        {canEdit && (
          <Box>
            <Button
              component={Link}
              to={`/studies/edit/${study.id}`}
              variant="outlined"
              startIcon={<EditIcon />}
              sx={{ mr: 1 }}
            >
              Edit
            </Button>
            {isAdmin && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="Overview" />
          <Tab label="Compare" />
        </Tabs>
      </Box>

      {tab === 1 && (
        <CohortComparison scopeType="study" scopeId={study.id} projects={projects} />
      )}

      {tab === 0 && (
      <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Study Number
            </Typography>
            <Typography variant="body1">{study.study_number ?? '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography variant="caption" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body1">{study.name}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body1">{study.description || '—'}</Typography>
          </Grid>
          {study.comments && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Comments
              </Typography>
              <Typography variant="body1">{study.comments}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6">Projects in this study ({projects.length})</Typography>
        {totalSpecimens > 0 && (
          <Button
            component={Link}
            to={`/specimens?study_id=${study.id}`}
            variant="outlined"
            size="small"
          >
            View {totalSpecimens.toLocaleString()} specimens
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {projects.length} project{projects.length === 1 ? '' : 's'} ·{' '}
        {totalSpecimens.toLocaleString()} specimen{totalSpecimens === 1 ? '' : 's'} total. Assign a
        project to this study from the project's edit form.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Collaborator</TableCell>
              <TableCell>Disease</TableCell>
              <TableCell align="right">Specimens</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No projects assigned to this study yet
                </TableCell>
              </TableRow>
            ) : (
              projects.map((project) => (
                <TableRow key={project.id} hover>
                  <TableCell>
                    <MuiLink
                      component={Link}
                      to={`/projects/${project.id}`}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {formatProjectLabel(project)}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{project.pi_name || '—'}</TableCell>
                  <TableCell>{project.disease || '—'}</TableCell>
                  <TableCell align="right">{project.specimen_count}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </>
      )}
    </Box>
  );
};

export default StudyDetail;
